import { useState } from 'react';
import Navbar from '../components/Navbar';
import MobileHeader from '../components/MobileHeader';
import BackButton from '../components/BackButton';
import { Wallet as WalletIcon, ArrowUpRight, ArrowDownRight, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { mockTransactions } from '../mockData';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

import { fmtFixed, fmtNumber } from '../utils/format';
const Wallet = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('deposit');
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [copied, setCopied] = useState(false);
  const [transactions] = useState(mockTransactions);

  const walletAddress = '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb4';

  const handleCopy = () => {
    navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    toast.success('Address copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeposit = (e) => {
    e.preventDefault();
    if (!amount) {
      toast.error('Please enter an amount');
      return;
    }
    toast.success(`Deposit of $${fmtNumber(amount, 2)} initiated!`);
    setAmount('');
  };

  const handleWithdraw = (e) => {
    e.preventDefault();
    if (!amount || !address) {
      toast.error('Please fill in all fields');
      return;
    }
    toast.success(`Withdrawal of $${fmtNumber(amount, 2)} initiated!`);
    setAmount('');
    setAddress('');
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Navbar isDashboard={true} />
      <MobileHeader isDashboard={true} />

      <div className="pt-20 sm:pt-24 pb-20 sm:pb-12 px-3 sm:px-4 md:px-6">
        <div className="container mx-auto max-w-7xl">
          <BackButton label="Back" />
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Wallet <span className="gradient-text">Management</span></h1>
            <p className="text-gray-400 text-sm sm:text-base md:text-lg">Deposit and withdraw funds securely</p>
          </div>

          {/* Balance Card */}
          <div className="glow-card p-4 sm:p-6 md:p-8 rounded-2xl mb-6 sm:mb-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 to-blue-600/10"></div>
            <div className="relative z-10">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-4 bg-gradient-to-br from-purple-600 to-blue-500 rounded-xl animate-pulse-glow">
                  <WalletIcon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Available Balance</p>
                  <h2 className="text-4xl font-bold">${user?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '125,430.50'}</h2>
                </div>
              </div>
              <div className="flex items-center space-x-2 mt-4">
                <div className="flex-1 bg-gray-900/50 p-3 rounded-lg">
                  <p className="text-gray-400 text-xs mb-1">Wallet Address</p>
                  <p className="font-mono text-sm">{walletAddress}</p>
                </div>
                <Button
                  onClick={handleCopy}
                  variant="ghost"
                  size="icon"
                  className="neo-btn neo-btn-secondary"
                >
                  {copied ? <Check className="h-5 w-5 text-green-400" /> : <Copy className="h-5 w-5" />}
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Deposit/Withdraw Panel */}
            <div className="lg:col-span-2">
              <div className="glow-card p-6 rounded-2xl">
                {/* Tabs */}
                <div className="flex space-x-2 mb-6">
                  <button
                    onClick={() => setActiveTab('deposit')}
                    className={`flex-1 py-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                      activeTab === 'deposit'
                        ? 'bg-gradient-to-r from-green-600 to-emerald-500 text-white'
                        : 'bg-gray-900/50 text-gray-400 hover:text-white'
                    }`}
                  >
                    <ArrowDownRight className="h-5 w-5" />
                    <span>Deposit</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('withdraw')}
                    className={`flex-1 py-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                      activeTab === 'withdraw'
                        ? 'bg-gradient-to-r from-red-600 to-pink-500 text-white'
                        : 'bg-gray-900/50 text-gray-400 hover:text-white'
                    }`}
                  >
                    <ArrowUpRight className="h-5 w-5" />
                    <span>Withdraw</span>
                  </button>
                </div>

                {/* Deposit Form */}
                {activeTab === 'deposit' && (
                  <div>
                    <h2 className="text-2xl font-bold mb-6">Deposit Funds</h2>
                    <p className="text-gray-400 mb-6">
                      Send cryptocurrency to your wallet address or deposit via bank transfer
                    </p>

                    <form onSubmit={handleDeposit} className="space-y-6">
                      <div>
                        <Label className="text-gray-300 mb-2 block">Amount (USD)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          className="bg-gray-900/50 border-purple-500/30 text-white focus:border-purple-500 text-lg"
                          required
                        />
                      </div>

                      <div className="bg-gray-900/30 p-4 rounded-xl space-y-3">
                        <h3 className="font-semibold text-sm text-gray-400">Deposit Methods</h3>
                        <div className="space-y-2">
                          <div className="p-3 bg-gray-900/50 rounded-lg hover:bg-gray-900/70 cursor-pointer transition-colors">
                            <p className="font-semibold">Cryptocurrency</p>
                            <p className="text-xs text-gray-400">Instant deposit • No fees</p>
                          </div>
                          <div className="p-3 bg-gray-900/50 rounded-lg hover:bg-gray-900/70 cursor-pointer transition-colors">
                            <p className="font-semibold">Bank Transfer</p>
                            <p className="text-xs text-gray-400">1-3 business days • 0.5% fee</p>
                          </div>
                          <div className="p-3 bg-gray-900/50 rounded-lg hover:bg-gray-900/70 cursor-pointer transition-colors">
                            <p className="font-semibold">Credit/Debit Card</p>
                            <p className="text-xs text-gray-400">Instant • 2.9% fee</p>
                          </div>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        className="w-full neo-btn bg-gradient-to-r from-green-600 to-emerald-500 text-lg py-6 font-semibold"
                      >
                        Continue Deposit
                      </Button>
                    </form>
                  </div>
                )}

                {/* Withdraw Form */}
                {activeTab === 'withdraw' && (
                  <div>
                    <h2 className="text-2xl font-bold mb-6">Withdraw Funds</h2>
                    <p className="text-gray-400 mb-6">
                      Withdraw your funds to an external wallet or bank account
                    </p>

                    <form onSubmit={handleWithdraw} className="space-y-6">
                      <div>
                        <Label className="text-gray-300 mb-2 block">Amount (USD)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          className="bg-gray-900/50 border-purple-500/30 text-white focus:border-purple-500 text-lg"
                          required
                        />
                        <p className="text-xs text-gray-400 mt-2">
                          Available: ${user?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '125,430.50'}
                        </p>
                      </div>

                      <div>
                        <Label className="text-gray-300 mb-2 block">Destination Address</Label>
                        <Input
                          type="text"
                          placeholder="0x..."
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          className="bg-gray-900/50 border-purple-500/30 text-white focus:border-purple-500"
                          required
                        />
                      </div>

                      <div className="bg-gray-900/30 p-4 rounded-xl">
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-400">Withdrawal Amount</span>
                          <span className="font-semibold">${amount ? fmtNumber(amount, 2) : '0.00'}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-400">Network Fee</span>
                          <span className="text-gray-400">$2.50</span>
                        </div>
                        <div className="border-t border-gray-700 pt-2 mt-2">
                          <div className="flex justify-between">
                            <span className="font-semibold">You'll Receive</span>
                            <span className="font-semibold text-lg">
                              ${amount ? fmtFixed((parseFloat(amount) - 2.5), 2) : '0.00'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        className="w-full neo-btn bg-gradient-to-r from-red-600 to-pink-500 text-lg py-6 font-semibold"
                      >
                        Withdraw Funds
                      </Button>
                    </form>
                  </div>
                )}
              </div>
            </div>

            {/* Transaction History */}
            <div className="lg:col-span-1">
              <div className="glow-card p-6 rounded-2xl">
                <h2 className="text-xl font-bold mb-6">Recent Activity</h2>
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="p-3 bg-gray-900/50 rounded-lg hover:bg-gray-900/70 transition-colors">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className={`p-2 rounded-lg ${tx.type === 'buy' || tx.type === 'deposit' ? 'bg-green-600/20' : 'bg-red-600/20'}`}>
                          {tx.type === 'buy' || tx.type === 'deposit' ? (
                            <ArrowDownRight className="h-4 w-4 text-green-400" />
                          ) : (
                            <ArrowUpRight className="h-4 w-4 text-red-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm capitalize">{tx.type}</p>
                          <p className="text-xs text-gray-400">{tx.crypto}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm">${tx.total.toLocaleString('en-US')}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">{new Date(tx.date).toLocaleDateString()}</span>
                        <span className={`px-2 py-0.5 rounded-full ${tx.status === 'completed' ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'}`}>
                          {tx.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wallet;
